import { useEffect, useId, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Lottie from './Lottie'
import GlassOverlay from './GlassOverlay'
import CloseGlyph from './CloseGlyph'
import LiquidGlassButton from './LiquidGlassButton'
import { IconCartPlus } from './scan/ScanIcons'
import { socialService, cartsService, itemsService } from '../services/api'
import { useNotifications } from '../contexts/NotificationContext'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { formatEuroFr } from '../utils/editProductOutcome'
import { articleCountLabel, previewItemRows } from '../utils/sharedCartPresentation'
import '../styles/scrollbar.css'
import './SharedCartPreview.css'

function authorizedSharedItemPayload(item) {
  const imageUrl = String(item?.image_url || '')
  const persistable = /^https?:\/\//i.test(imageUrl)
    && !/supabase\.(co|in)/i.test(imageUrl)
    && !/\/api\/media\?/i.test(imageUrl)
  return {
    name: item?.name || '',
    link: item?.link || '',
    price: item?.price ?? 0,
    quantity: item?.quantity || 1,
    promo: item?.promo || '',
    certainty_label: item?.certainty_label || '',
    image_url: persistable ? imageUrl : '',
    image_key: '',
  }
}

function ProductThumb({ src, name }) {
  const [failed, setFailed] = useState(!src)
  return (
    <div className="scp-thumb" aria-hidden="true">
      {failed ? (
        <span className="scp-thumb-fallback" />
      ) : (
        <img src={src} alt="" onError={() => setFailed(true)} />
      )}
      <span className="sr-only">{name}</span>
    </div>
  )
}

function PreviewHeader({ title, titleId, onClose, closeRef }) {
  return (
    <header className="scp-header">
      <h2 id={titleId} title={title}>{title}</h2>
      <button
        ref={closeRef}
        type="button"
        className="scp-close"
        onClick={onClose}
        aria-label="Fermer l’aperçu"
      >
        <CloseGlyph />
      </button>
    </header>
  )
}

export default function SharedCartPreview({
  isOpen = true,
  cartId,
  cartName,
  onClose,
  demoData = null,
}) {
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const reducedMotion = usePrefersReducedMotion()
  const closeRef = useRef(null)
  const savingRef = useRef(false)
  const titleId = useId()
  const [successAnimation, setSuccessAnimation] = useState(null)
  const [cartAnimation, setCartAnimation] = useState(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')
  const successLottieRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    fetch('/Succes.json')
      .then((res) => res.json())
      .then((payload) => {
        if (!cancelled) setSuccessAnimation(payload)
      })
      .catch(() => {})
    fetch('/Cadis_animation.json')
      .then((res) => res.json())
      .then((payload) => {
        if (!cancelled) setCartAnimation(payload)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['shared-cart', cartId],
    queryFn: () => socialService.getSharedCartDetails(cartId),
    enabled: !!cartId && !demoData,
    retry: false,
  })

  const cartData = demoData || data
  const unavailable = error?.response?.status === 404 || error?.response?.status === 403
  const rows = previewItemRows(cartData?.items)

  const saveCartMutation = useMutation({
    mutationFn: async () => {
      if (!cartData) return null
      const newName = `${cartData.name} (partagé)`
      const newCart = await cartsService.create(newName, cartData.budget || null)
      if (cartData.items?.length) {
        for (const item of cartData.items) {
          const itemData = authorizedSharedItemPayload(item)
          const storageKey = String(item.image_key || '')
          if (storageKey && !/^https?:|^data:/i.test(storageKey)) {
            try {
              const copiedImage = await socialService.copySharedCartImage(storageKey)
              itemData.image_key = copiedImage.key || ''
            } catch {
              itemData.image_key = ''
            }
          }
          await itemsService.create(newCart.id, itemData)
        }
      }
      return { newCart, itemsCount: cartData.items?.length || 0 }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carts'] })
      setSaveError('')
      setShowSuccess(true)
      successLottieRef.current?.goToAndPlay?.(0)
      window.setTimeout(() => {
        setShowSuccess(false)
        savingRef.current = false
        onClose?.()
      }, reducedMotion ? 400 : 1800)
    },
    onError: () => {
      savingRef.current = false
      setSaveError('Enregistrement impossible. Réessayez.')
      addNotification({
        type: 'error',
        message: 'Impossible d’enregistrer ce panier dans vos paniers.',
      })
    },
  })

  const saving = saveCartMutation.isPending || saveCartMutation.isLoading
  const title = cartName || cartData?.name || 'Aperçu du panier'
  const loading = (isLoading || isFetching) && !demoData && !cartData
  const itemCount = cartData?.items_count || rows.length

  const requestSave = () => {
    if (savingRef.current || saving || showSuccess || demoData || !cartData) return
    savingRef.current = true
    setSaveError('')
    saveCartMutation.mutate()
  }

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={onClose}
      labelledBy={titleId}
      returnFocusRef={closeRef}
      panelClassName="shared-cart-preview-panel"
      rootClassName="shared-cart-preview-root"
      followPointer={false}
    >
      <div className={`scp${loading ? ' is-loading' : ''}${cartData && !loading ? ' is-ready' : ''}`}>
        <PreviewHeader title={title} titleId={titleId} onClose={onClose} closeRef={closeRef} />
        {loading ? (
          <div className="scp-state scp-state-compact">
            {cartAnimation && !reducedMotion ? (
              <Lottie animationData={cartAnimation} loop autoplay style={{ width: 56, height: 56 }} />
            ) : (
              <span className="scp-cart-glyph" aria-hidden="true">
                <IconCartPlus />
              </span>
            )}
            <p>Chargement du panier</p>
          </div>
        ) : unavailable ? (
          <div className="scp-state scp-state-compact">
            <p>Ce panier n’est plus accessible. Il a peut-être été supprimé.</p>
          </div>
        ) : error ? (
          <div className="scp-state scp-state-compact">
            <p className="scp-error">Impossible de charger ce panier.</p>
            <button type="button" className="scp-retry" onClick={() => refetch()}>
              Réessayer
            </button>
          </div>
        ) : showSuccess ? (
          <div className="scp-state scp-state-compact">
            {successAnimation && !reducedMotion ? (
              <Lottie
                lottieRef={successLottieRef}
                animationData={successAnimation}
                loop={false}
                autoplay
                style={{ width: 88, height: 88 }}
              />
            ) : null}
            <p>Enregistré dans vos paniers.</p>
          </div>
        ) : cartData ? (
          <>
            <p className="scp-summary">
              <strong className="scp-name">{cartData.name || title}</strong>
              <span aria-hidden="true"> · </span>
              {articleCountLabel(itemCount)}
              <span aria-hidden="true"> · </span>
              <span className="scp-amount">{formatEuroFr(cartData.total)}</span>
            </p>
            <div className="scp-list lg-scroll" role="list">
              {rows.length === 0 ? (
                <p className="scp-empty">Ce panier est vide.</p>
              ) : rows.map((item) => (
                <div key={item.id} className="scp-row" role="listitem">
                  <ProductThumb src={item.image_url} name={item.name} />
                  <div className="scp-row-copy">
                    <p className="scp-row-name" title={item.name}>{item.name}</p>
                    <p className="scp-row-meta">
                      {item.quantity}&nbsp;× {formatEuroFr(item.unit)}
                    </p>
                  </div>
                  <span className="scp-row-sub">{formatEuroFr(item.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="scp-actions">
              {saveError ? <p className="scp-error" role="status">{saveError}</p> : null}
              <LiquidGlassButton
                onClick={requestSave}
                disabled={saving || showSuccess || !!demoData}
                aria-busy={saving || undefined}
              >
                <span className="scp-save-label">
                  <IconCartPlus />
                  <span>{saving ? 'Enregistrement…' : 'Enregistrer dans mes paniers'}</span>
                </span>
              </LiquidGlassButton>
            </div>
          </>
        ) : null}
      </div>
    </GlassOverlay>
  )
}
